import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { useCart } from '../../../contexts/CartContext';

const PAYMENT_OPTIONS = [
  {
    key: 'paypal',
    name: 'PayPal',
    description: 'Checkout quickly using your PayPal balance or linked cards.',
    logo: 'https://www.paypalobjects.com/webstatic/icon/pp258.png'
  },
  {
    key: 'stripe',
    name: 'Stripe',
    description: 'Secure card payments powered by Stripe sandbox environment.',
    logo: 'https://brandslogos.com/wp-content/uploads/images/large/stripe-logo.png'
  },
  {
    key: 'razorpay',
    name: 'Razorpay',
    description: 'Popular Indian gateway supporting UPI, cards, and net banking.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Razorpay_logo.png'
  }
];

const PaymentSelectionPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    refreshCart,
    isLoading,
    voucherCode
  } = useCart();

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isInitiating, setIsInitiating] = useState(false);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const subtotalAmount = useMemo(() => {
    const total = cartItems.reduce((sum, item) => {
      return sum + Number(item.course?.price || 0);
    }, 0);
    return total.toFixed(2);
  }, [cartItems]);

  const [finalAmount, setFinalAmount] = useState('0.00');

  useEffect(() => {
    const recalculatePaymentTotal = async () => {
      const subtotal = Number(subtotalAmount);
      const trimmedCode = (voucherCode || '').trim();

      if (!trimmedCode || !cartItems.length) {
        setFinalAmount(subtotal.toFixed(2));
        return;
      }

      try {
        const response = await api.vouchers.list({ code: trimmedCode });
        const vouchers = response.data?.data || response.data || [];

        if (!Array.isArray(vouchers) || vouchers.length === 0) {
          setFinalAmount(subtotal.toFixed(2));
          return;
        }

        const lowerCode = trimmedCode.toLowerCase();
        const voucher =
          vouchers.find((v) => v.code?.toLowerCase() === lowerCode) || vouchers[0];

        if (!voucher || voucher.deleted || voucher.status !== 'active') {
          setFinalAmount(subtotal.toFixed(2));
          return;
        }

        const now = new Date();
        const start = voucher.start_date ? new Date(voucher.start_date) : null;
        const end = voucher.end_date ? new Date(voucher.end_date) : null;

        if ((start && now < start) || (end && now > end)) {
          setFinalAmount(subtotal.toFixed(2));
          return;
        }

        let applicableTotal = 0;

        if (voucher.scope === 'course') {
          const uniqueCourseIds = Array.from(
            new Set(cartItems.map((item) => item.courseId || item.course?.id).filter(Boolean))
          );

          if (
            uniqueCourseIds.length !== 1 ||
            Number(uniqueCourseIds[0]) !== Number(voucher.course_id)
          ) {
            setFinalAmount(subtotal.toFixed(2));
            return;
          }

          cartItems.forEach((item) => {
            const price = Number(item.course?.price || 0);
            if (item.courseId === voucher.course_id || item.course?.id === voucher.course_id) {
              applicableTotal += price;
            }
          });
        } else {
          cartItems.forEach((item) => {
            const price = Number(item.course?.price || 0);
            applicableTotal += price;
          });
        }

        if (applicableTotal <= 0) {
          setFinalAmount(subtotal.toFixed(2));
          return;
        }

        let discountAmount = 0;
        if (voucher.discount_type === 'percentage') {
          discountAmount = (applicableTotal * Number(voucher.discount_value || 0)) / 100;
        } else {
          discountAmount = Number(voucher.discount_value || 0);
        }

        if (!Number.isFinite(discountAmount) || discountAmount <= 0) {
          setFinalAmount(subtotal.toFixed(2));
          return;
        }

        discountAmount = Math.min(discountAmount, applicableTotal);
        const final = Math.max(subtotal - discountAmount, 0);
        setFinalAmount(final.toFixed(2));
      } catch (error) {
        console.error('Error calculating payment total with voucher:', error);
        setFinalAmount(subtotal.toFixed(2));
      }
    };

    recalculatePaymentTotal();
  }, [cartItems, subtotalAmount, voucherCode]);

  useEffect(() => {
    if (!isLoading && cartItems.length === 0) {
      toast.info('Your cart is empty. Add a course to continue.');
      navigate('/user/cart', { replace: true });
    }
  }, [cartItems, isLoading, navigate]);

  const handleProviderSelect = async (providerKey) => {
    setSelectedProvider(providerKey);
    setIsInitiating(true);
    try {
      const response = await api.payments.initiate(providerKey, voucherCode || null);
      const redirectUrl = response.data?.data?.redirectUrl;
      if (!redirectUrl) {
        throw new Error('Missing redirect URL.');
      }

      toast.success(
        providerKey === 'paypal'
          ? 'Redirecting to PayPal sandbox...'
          : `Redirecting to ${providerKey.toUpperCase()} sandbox...`
      );

      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Payment initiate error', error);
      const errorMessage = error.response?.data?.message || 'Failed to initiate payment.';
      toast.error(errorMessage);
      setSelectedProvider(null);
      setIsInitiating(false);
    }
  };

  if (cartItems.length === 0) {
    return null;
  }

  return (
    <div className="container-fluid px-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Col md={10}>
          <div>
            <h4 className="fw-bold text-dark mb-1">Choose Payment Method</h4>
            <p className="text-muted mb-0">
              Total payable amount: <strong>₹{finalAmount}</strong>
            </p>
          </div>
        </Col>
        <Col md={2}>
          <Button variant="secondary" className="text-decoration-none" onClick={() => navigate('/user/cart')}>
            <i className="fas fa-arrow-left me-2"></i>
            Back to Cart
          </Button>
        </Col>
      </div>

      <Row className="g-4">
        {PAYMENT_OPTIONS.map((option) => (
          <Col key={option.key} md={4} sm={12}>
            <Card className={`border-0 shadow-sm h-100 ${selectedProvider === option.key ? 'border border-success' : ''}`}>
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '56px', height: '56px' }}>
                    <img src={option.logo} alt={`${option.name} logo`} style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }} />
                  </div>
                  <div>
                    <h5 className="fw-semibold mb-0">{option.name}</h5>
                    <small className="text-muted">{option.description}</small>
                  </div>
                </div>
                <div className="mt-auto">
                  <Button
                    variant={selectedProvider === option.key ? 'success' : 'outline-success'}
                    className="w-100 fw-semibold"
                    disabled={isInitiating}
                    onClick={() => handleProviderSelect(option.key)}
                  >
                    {isInitiating && selectedProvider === option.key ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Redirecting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-arrow-right me-2"></i>
                        Pay with {option.name}
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default PaymentSelectionPage;

